import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState } from "features/Application/globaltypes/redux";
import {
  isUserDeleted,
  type PerformanceWithAccountFilter,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  getLinkNavigationProps,
} from "features/Library/Common/utils/performanceUtils";
import { type RemedialTrainingUserItem, ThreatDefenseFlowStatusEnum } from "../types";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { getRemedialTrainingUsers } from "../../queries/threatDefensePerformanceQueries";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type IconDefinition, faCircleCheck, faClock } from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import { type ColumnOption } from "interfaces/columnOptions";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import styles from "./UsersTable.module.scss";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { type Dispatch } from "redux";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";

const StatusToIcon: { [key in ThreatDefenseFlowStatusEnum]: IconDefinition } = {
  [ThreatDefenseFlowStatusEnum.NOT_STARTED]: faCircle,
  [ThreatDefenseFlowStatusEnum.IN_PROGRESS]: faClock,
  [ThreatDefenseFlowStatusEnum.COMPLETED]: faCircleCheck,
};

type Props = PropsFromRedux & {
  filter: PerformanceWithAccountFilter;
  campaignId: number;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

const UsersTable = ({
  filter,
  campaignId,
  accountId,
  setHasExportData,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}: Props) => {
  const columns = useMemo<ColumnOption<RemedialTrainingUserItem>[]>(
    () => [
      {
        name: "Name",
        width: 5,

        render(item) {
          const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
          const shouldShowLink =
            !isUserDeleted(item.UserName) && (!includeAccounts || childAccountEnabled || !childAccountId);
          const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
            item.AccountId,
            accountId,
            childAccountId,
            hasCreateAccountsAddOn,
          );
          const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
            childAccountEnabled,
            isUserFromCurrentAccount,
            childAccountId,
            fallbackAccountId: item.AccountId,
          });
          const userName = (
            <Tooltip target={<TextTruncate>{item.UserName}</TextTruncate>} content={`${item.UserName}`} />
          );
          return (
            <div>
              {shouldShowLink ? (
                <MoboLink
                  accountId={linkAccountId}
                  to={`/${RouteNames.peopleUsers}/${item.UserId}`}
                  className={styles.linkButton}
                  target={linkTarget}
                >
                  {userName}
                </MoboLink>
              ) : (
                userName
              )}
            </div>
          );
        },
      },
      {
        name: "Job Title",
        width: 5,

        render(item) {
          return (
            <Tooltip target={<TextTruncate>{item.JobTitle || "-"}</TextTruncate>} content={item.JobTitle || "-"} />
          );
        },
      },
      {
        name: "Department",
        width: 5,

        render(item) {
          return (
            <Tooltip target={<TextTruncate>{item.Department || "-"}</TextTruncate>} content={item.Department || "-"} />
          );
        },
      },
      {
        name: "Email",
        width: 6,

        render(item) {
          return <Tooltip target={<TextTruncate>{item.Email}</TextTruncate>} content={item.Email} />;
        },
      },
      {
        width: 4,
        name: "Status",

        render(item) {
          return (
            <div className={styles.icon}>
              <FontAwesomeIcon icon={StatusToIcon[item.Status]} className={styles[`${StatusToIcon[item.Status]}`]} />
              {item.Status}
            </div>
          );
        },
      },
    ],
    [accountId, includeAccounts, childAccountId, hasCreateAccountsAddOn],
  );

  const usersQuery = useQuery({
    queryFn: getRemedialTrainingUsers,
    queryKey: ["remedialTrainingUsers", filter, campaignId],
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: usersQuery.isSuccess && usersQuery.data.length > 0,
      tooltipContent: "No data available for export. This remedial training contains no data.",
    });
  }, [setHasExportData, usersQuery]);

  return (
    <RequestStatusRenderer state={usersQuery.status}>
      {usersQuery.isSuccess && usersQuery.data && (
        <GenericPerformanceList rows={usersQuery.data} columns={columns} filter={filter} />
      )}
    </RequestStatusRenderer>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountId: state.userProfile.accountId,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UsersTable);
export default ConnectedComponent;
